<template>
  <div class="aspect-video w-full">
    <vue-vimeo-player
        v-if="isVisible"
        v-show="isPlayerReady"
        ref="playerRef"
        id="player"
        :video-id="getVideoSrc"
        :events-to-emit="playerEvents"
        @ready="onReady"
        @play="onPlay"
        allow="autoplay; fullscreen; picture-in-picture"
        mozallowfullscreen webkitallowfullscreen allowfullscreen
        :controls="true"/>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Player from "@vimeo/player";
import { vueVimeoPlayer } from 'vue-vimeo-player';
import { reportVideoPlayEvent } from "@/composables/user-activity-monitor";

const props = defineProps<{ vimeoID: any; }>();
const emit = defineEmits<{ ready: [ player: Player ] }>();
const isVisible = computed( () => !!props.vimeoID );
const getVideoSrc = computed( () => {
  if ( typeof props.vimeoID === 'string' ) {
    if ( props.vimeoID.includes( 'http' ) || props.vimeoID.includes( '.com' ) ) {
      const parts = props.vimeoID.split( '/' );
      return parts[ parts.length - 1 ];
    }
  }

  return props.vimeoID;
} );

const playerRef = ref();

const isPlayerReady = ref< boolean >( false );
const onReady = ( _playerInstance: Player ) => {
  playerRef.value.player = _playerInstance;

  // Unmute video on mobile since it's user-initiated
  _playerInstance.setMuted( false ).catch( () => {
    // Fallback if unmuting fails due to autoplay policies
  } );

  isPlayerReady.value = true;
  emit('ready', _playerInstance);
};

const playerEvents = [ 'play', 'pause', 'ended', 'error', 'loaded' ];

const hasPlayed = ref<boolean>( false );
const vimeoInfo = ref( {
  id: getVideoSrc.value,
  title: '',
  duration: 0
} );

const onPlay = () => {
  Promise.all( [
    playerRef.value.player.getVideoId(),
    playerRef.value.player.getVideoTitle(),
    playerRef.value.player.getDuration()
  ] ).then( ( [ id, title, duration ] ) => {
    vimeoInfo.value = { id, title, duration };
  } );
  if ( !hasPlayed.value ) {
    reportVideoPlayEvent( vimeoInfo.value.id );
  }
  hasPlayed.value = true;
};

watch( () => props.vimeoID, () => {
  hasPlayed.value = false;
} );

function pauseVideo() {
  playerRef.value.pause();
}

async function playVideo() {
  // Ensure video is unmuted before playing (user-initiated action)
  await playerRef.value.player.setMuted( false );
  await playerRef.value.player.play();
}

defineExpose( { pauseVideo, playVideo } );
</script>

<style>
#player iframe {
  aspect-ratio: 16 / 9 !important;
  width: 100% !important;
  height: auto !important;
  border-radius: 0.5rem !important;
}
</style>
